import React, { useState, useRef } from "react";
import { streamSSE, downloadExport } from "@/lib/api";
import Markdown from "@/components/Markdown";
import { toast } from "sonner";
import { GitBranchPlus, Sparkles, Loader2, Download, ShieldAlert } from "lucide-react";

const CATEGORIES = [
  { code: "labeling",    label: "Labeling / IFU change" },
  { code: "technology",  label: "Technology or design change" },
  { code: "materials",   label: "Materials change" },
  { code: "performance", label: "Performance-standards change" },
  { code: "combination", label: "Combination / multiple areas" },
  { code: "unspecified", label: "Not sure" },
];

export default function ChangeControlPanel() {
  const [deviceName, setDeviceName] = useState("");
  const [kNumber, setKNumber] = useState("");
  const [intendedUse, setIntendedUse] = useState("");
  const [change, setChange] = useState("");
  const [category, setCategory] = useState("unspecified");
  const [streaming, setStreaming] = useState(false);
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const outRef = useRef(null);

  const run = async () => {
    if (!deviceName.trim() || !intendedUse.trim() || !change.trim()) {
      return toast.error("Device name, intended use, and change description are all required");
    }
    setStreaming(true); setContent(""); setTitle("");
    let buf = "";
    try {
      await streamSSE("/change-control/analyze", {
        device_name: deviceName,
        original_k_number: kNumber,
        original_intended_use: intendedUse,
        change_description: change,
        change_category: category,
      }, (frame) => {
        if (frame.title) setTitle(frame.title);
        if (frame.delta) {
          buf += frame.delta; setContent(buf);
          if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
        }
        if (frame.done) toast.success("Change-control analysis ready");
        if (frame.error) toast.error(frame.error);
      });
    } catch { toast.info("Stream ended. Content is saved."); }
    finally { setStreaming(false); }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="overline mb-2 text-[#2563EB]">CHANGE CONTROL · FDA 2017 GUIDANCE</div>
        <h1 className="font-serif text-3xl font-semibold tracking-tight lg:text-4xl">
          Letter to File vs. Special 510(k).
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-500">
          Apply FDA's 2017 "Deciding When to Submit a 510(k) for a Change" decision-tree guidance
          to any post-market change. Get a clear recommendation with rationale and required DHF
          documentation.
        </p>
      </div>

      <div className="mb-6 flex items-start gap-3 border border-amber-500/40 bg-amber-500/8 p-4 text-sm leading-relaxed text-amber-100/90">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
        Preliminary decision-support only. Final classification rests with your regulatory team.
        Consider a Q-Sub if uncertainty is high.
      </div>

      <div className="grid gap-8 lg:grid-cols-[420px_1fr]">
        <div className="border border-slate-200 bg-white p-6 space-y-4">
          <div>
            <label className="overline mb-2 block text-slate-500">Device name *</label>
            <input data-testid="cc-device" value={deviceName} onChange={(e) => setDeviceName(e.target.value)}
              placeholder="CardioSense Pulse Ox v1" className="w-full border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm focus:border-[#2563EB] focus:outline-none" />
          </div>
          <div>
            <label className="overline mb-2 block text-slate-500">Original K number (optional)</label>
            <input data-testid="cc-knumber" value={kNumber} onChange={(e) => setKNumber(e.target.value)}
              placeholder="K221234" className="w-full border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm focus:border-[#2563EB] focus:outline-none" />
          </div>
          <div>
            <label className="overline mb-2 block text-slate-500">Original intended use *</label>
            <textarea data-testid="cc-iu" value={intendedUse} onChange={(e) => setIntendedUse(e.target.value)} rows={2}
              placeholder="Continuous SpO2 monitoring in adult hospital patients"
              className="w-full resize-none border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm focus:border-[#2563EB] focus:outline-none" />
          </div>
          <div>
            <label className="overline mb-2 block text-slate-500">Change category</label>
            <select data-testid="cc-category" value={category} onChange={(e) => setCategory(e.target.value)}
              className="w-full border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm text-slate-900 focus:border-[#2563EB] focus:outline-none">
              {CATEGORIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="overline mb-2 block text-slate-500">Proposed change *</label>
            <textarea data-testid="cc-change" value={change} onChange={(e) => setChange(e.target.value)} rows={5}
              placeholder="Describe what's changing: new sensor supplier, updated ML model, expanded population, new indication, packaging change, etc."
              className="w-full resize-none border border-slate-200 bg-[#FAFAF7] px-3 py-2.5 text-sm focus:border-[#2563EB] focus:outline-none" />
          </div>
          <button data-testid="cc-run-btn" onClick={run} disabled={streaming}
            className="inline-flex w-full items-center justify-center gap-2 bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-50">
            {streaming ? <><Loader2 className="h-4 w-4 animate-spin" /> Analyzing…</> : <><Sparkles className="h-4 w-4" /> Run change-control analysis</>}
          </button>
        </div>

        <div className="border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
            <div className="min-w-0 flex-1">
              <div className="overline text-[#2563EB]">DECISION REPORT</div>
              <div className="truncate font-serif text-lg font-semibold">{title || "Fill the form to start"}</div>
            </div>
            {content && (
              <button onClick={() => downloadExport(title || "Change-Control Decision", content, "pdf")}
                className="inline-flex items-center gap-1.5 border border-slate-200 px-3 py-2 text-xs text-slate-700 hover:border-[#2563EB]">
                <Download className="h-3.5 w-3.5" /> PDF
              </button>
            )}
          </div>
          <div ref={outRef} data-testid="cc-output" className="max-h-[75vh] overflow-y-auto p-6 lg:p-10">
            {!content && !streaming && (
              <div className="flex h-64 flex-col items-center justify-center text-slate-400">
                <GitBranchPlus className="mb-4 h-8 w-8" strokeWidth={1.5} />
                <div className="overline">DECISION APPEARS HERE</div>
              </div>
            )}
            {streaming && !content && (
              <div className="flex items-center gap-2 text-slate-500">
                <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
                <span className="ml-2 text-sm">Walking the decision tree…</span>
              </div>
            )}
            {content && <Markdown>{content}</Markdown>}
          </div>
        </div>
      </div>
    </div>
  );
}
